import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { VoteHeader } from './VoteHeader'
import { ErrorBanner } from '../common/ErrorBanner'
import { InfoBanner } from '../common/InfoBanner'
import { LoadingPanel } from '../common/LoadingPanel'
import { VoteSubmitted } from './VoteSubmitted'
import { VoteBiography } from './VoteBiography'
import { SingleGroupVote } from './SingleGroupVote'
import { MultiGroupVote } from './MultiGroupVote'
import { AgmStatus } from '../agm/AgmStatus';

export const Vote = ({ voter, ballot, previewId, bioId, validateState, isPreview, isValidatedAdmin, onUpdateAuth }) => {
    const [isLoading, setLoading] = useState(false)
    const [voteError, setVoteError] = useState(null)
    const [voteInfo, setVoteInfo] = useState(null)
    const [isJustSubmitted, setJustSubmitted] = useState(false)
    const [currentGroup, setCurrentGroup] = useState(null)
    const [currentGroupVote, setCurrentGroupVote] = useState(null)
    const [groupIds, setGroupIds] = useState(null)
    const [votingType, setVotingType] = useState(null)
    const [isShowVoting, setShowVoting] = useState(false)
    const [isShowBio, setShowBio] = useState(false)
    const [bioBallotOption, setBioBallotOption] = useState(null)
    const [agmGroupStatus, setAgmGroupStatus] = useState(null)

    const getGroup = (groupId = null, previewVotes = null, onGetGroup) => {
        axios.post('/vote/group', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            previewId: previewId,
            receipt: voter.receipt,
            groupId: groupId
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    // Do we need to add any preview votes to the group options first?
                    let currentGroup = response.data.payload.group
                    if (previewVotes) {
                        for (let i = 0; i < previewVotes.length; i++) {
                            // Find option
                            let option = currentGroup.options.find(option => option.ballotOptionId === previewVotes[i].ballotOptionId)
                            if (option) {
                                option.score = previewVotes[i].score
                                option.textValue = previewVotes[i].textValue
                            }
                        }
                    }

                    setCurrentGroup(currentGroup)
                    setCurrentGroupVote(response.data.payload.groupVote)
                    setGroupIds(response.data.payload.groupIds)
                    setVotingType(response.data.payload.votingType)
                    setShowVoting(true)
                    setLoading(false)

                    if (onGetGroup) {
                        onGetGroup()
                    }
                }
                else {
                    setVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const getAgmGroupStatus = (onComplete) => {
        axios.post('/vote/agmGroupStatus', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            previewId: previewId,
            receipt: voter.receipt
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setAgmGroupStatus(response.data.payload)

                    if (onComplete) {
                        onComplete()
                    }
                }
                else {
                    setVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    // AGM group status
    useEffect(() => {
        if (agmGroupStatus && agmGroupStatus.statusStatement) {
            setVoteInfo(agmGroupStatus.statusStatement)
        }
    }, [agmGroupStatus]);

    const getBio = () => {
        axios.post('/vote/bio', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            previewId: previewId,
            receipt: voter.receipt,
            optionId: parseInt(bioId)
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setBioBallotOption(response.data.payload)
                    setShowBio(true)
                    setLoading(false)
                }
                else {
                    setVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    useEffect(() => {
        // Refresh vote component
        if (validateState) {
            refresh()
        }
    }, [validateState]);

    const refresh = (isSetLoading = true) => {
        setLoading(isSetLoading)

        // Clear banners
        setVoteError("")
        setVoteInfo("")
        setJustSubmitted(false)

        // Show bio (either ballot is open or its a preview)?
        if (bioId && (validateState === "BallotOpen" || isPreview)) {
            getBio();
            return
        }

        // Show voting page details?
        const isShowVoting = validateState === "BallotOpen"
            || validateState === "VoteSubmitted"
            || validateState === "BallotAgmOpen"
            || isValidatedAdmin
        setShowVoting(isShowVoting)

        // AGMs have their own status messages that supercede standard ballot closed etc
        if (ballot.isAgm && validateState !== "BallotOpen") {
            getAgmGroupStatus(() => {
                if (isShowVoting) {
                    getGroup();
                }
                else {
                    setLoading(false)
                }
            })
        }
        else {
            // Process status messages
            if (processStatus()) {
                setLoading(false)
            }

            // Only get group if showing vote
            if (isShowVoting) {
                getGroup();
            }
        }
    }

    const processStatus = () => {
        // Set not published
        if (validateState === "BallotNotPublished") {
            setVoteError("Ballot is not available for voting at this time. Please consult your Ballot manager.")
            return true
        }
        // Set pause message
        else if (validateState === "BallotPaused") {
            setVoteInfo(ballot.pauseMessage ? ballot.pauseMessage : "Ballot is paused")
            return true
        }
        // Set ballot closed
        else if (validateState === "BallotClosed") {
            setVoteError("Ballot is closed")
            return true
        }
        // Set ballot not open yet
        else if (validateState === "BallotNotOpenYet") {
            setVoteInfo("Ballot is not open for voting yet")
            return true
        }
    }

    const handleVoteError = (voteError) => {
        setVoteError(voteError)
    }

    const handleOptionsUpdate = (options) => {
        const updatedCurrentGroup = { ...currentGroup }
        updatedCurrentGroup.options = options
        setCurrentGroup(updatedCurrentGroup)
    }

    const handleSubmitVotes = (votes, isVotesSubmitted, onSubmitSuccess, onSubmitFailure) => {
        axios.post('/vote/submit', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            previewId: previewId,
            receipt: voter.receipt,
            groupId: currentGroup.groupId,
            votes: votes,
            isSubmitted: isVotesSubmitted
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setVoteError(null)

                    // Set submitted
                    if (isVotesSubmitted) {
                        // Get group again to refresh state
                        getGroup(null, null, onSubmitSuccess)

                        setJustSubmitted(true)
                    }
                    else if (onSubmitSuccess) {
                        onSubmitSuccess(votes)
                    }
                }
                else {
                    setVoteError(response.data.errorMessage)
                    // Check for invalid option IDs to flag
                    if (response.data.payload) {
                        const invalidOptions = response.data.payload.invalidOptionIds
                        const updatedCurrentGroup = { ...currentGroup }

                        for (let i = 0; i < updatedCurrentGroup.options.length; i++) {
                            updatedCurrentGroup.options[i].isError = invalidOptions.includes(updatedCurrentGroup.options[i].ballotOptionId)
                        }

                        setCurrentGroup(updatedCurrentGroup)
                    }

                    // Submit success
                    if (onSubmitFailure) {
                        onSubmitFailure(votes)
                    }
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const handleSubmit = (isVotesSubmitted, onSubmitSuccess, onSubmitFailure) => {
        // Get votes
        let votes = getVotes(isVotesSubmitted)

        // Submit votes
        handleSubmitVotes(votes, isVotesSubmitted, onSubmitSuccess, onSubmitFailure)
    }

    const handleAgmStatusUpdate = (oldStatus, newStatus) => {
        // Check for status changes where we don't want to show loading
        let isSetLoading = true
        if ((oldStatus === "AgmGroupOpen" && newStatus === "AgmGroupPaused") ||
            (oldStatus === "AgmGroupPaused" && newStatus === "AgmGroupOpen")) {
            isSetLoading = false
        }

        setLoading(isSetLoading)

        if (isSetLoading) {
            // Update voter auth with trigger refresh (and getAgmGroupStatus)
            onUpdateAuth()
        }
        else {
            // Only refresh AGM status
            getAgmGroupStatus()
        }
    }

    const getVotes = (isVotesSubmitted = true) => {
        var votes = []

        currentGroup.options.filter(o => isIncludeOptionForVote(o, currentGroup)).forEach((option) => {
            var vote = {
                ballotOptionId: option.ballotOptionId,
                ballotOptionName: option.ballotOptionName,
                groupId: currentGroup.groupId,
                score: getScoreForVote(option, currentGroup),
                textValue: option.textValue,
                isSubmitted: isVotesSubmitted,
                ibpsaGroup: option.ibpsaGroup
            }
            votes.push(vote)
        })

        return votes
    }

    const isIncludeOptionForVote = (option, group) => {
        // Hidden option? (e.g. Survey's with linked questions)
        if (option.isOptionHidden === 'true') {
            return false
        }

        // IBPSA includes the no votes (but exclude the proxy options as the standard check at bottom will work)
        if (group.groupType === "IBPSA" && option.ballotOptionName != "Attend" && option.ballotOptionName != "Proxy") {
            return true
        }

        // Questionnaires includes the no votes
        if (group.groupType === "Questionnaire" && option.score != null) {
            return true
        }

        // Survey text question?
        if (option.textValue && option.textValue != '') {
            return true
        }

        // Default to only include selected or values greater than 0
        return option.score > 0
    }

    const getScoreForVote = (option) => {
        // If we got this far through the option filter then it implies we want to register a 0 or more for each option
        return option.score ?? 0
    }

    let weightingLabel = null
    const voteWeight = voter.voteWeight
    if (voteWeight > 1 && currentGroupVote && !currentGroupVote.hasVoted) {
        // Update weighting label
        weightingLabel = ballot.isSplitVoting ?
            `Your voter account is authorised to vote up to ${voteWeight} times (${voteWeight - currentGroupVote.numberOfVotes} votes left)` :
            `Your voter account has a vote weighting of ${voteWeight}`
    }

    return (
        <div>
            <VoteHeader voter={voter}
                ballot={ballot}
                countdownDateUtc={agmGroupStatus?.openGroup?.closeTime}
                isPaused={agmGroupStatus?.openGroup?.pauseTime != null}
                isShowBorder={true}
            />
            {voteError && <ErrorBanner isSticky="true">{voteError}</ErrorBanner>}
            {voteInfo && <InfoBanner isSticky="true" isSetHtml="true">{voteInfo}</InfoBanner>}

            {ballot.isAgm &&
                <AgmStatus
                    ballotId={ballot.id}
                    onAgmStatusUpdate={handleAgmStatusUpdate}
                    authQueryParams={`vid=${voter.uniqueId}&key=${voter.guid}`} />
            }

            <LoadingPanel className="py-8 px-10 text-center"
                isLoading={isLoading} />

            {isShowBio && !isLoading &&
                <VoteBiography
                    bioBallotOption={bioBallotOption} />}
            {isShowVoting && !isLoading &&
                <div>
                    <div className="mb-4">
                        <h2>{voter.name}</h2>
                    </div>
                    {(isJustSubmitted || (currentGroupVote && currentGroupVote.hasVoted)) &&
                        <VoteSubmitted voter={voter}
                            ballot={ballot}
                            groupVote={currentGroupVote}
                            onVoteError={handleVoteError}
                            isJustSubmitted={isJustSubmitted}
                        />}
                    {weightingLabel &&
                        <InfoBanner>{weightingLabel}</InfoBanner>
                    }
                    {(votingType === "SingleGroup" ||
                        votingType === "Agm") &&
                        <SingleGroupVote
                            ballot={ballot}
                            getGroup={getGroup}
                            currentGroup={currentGroup}
                            currentGroupVote={currentGroupVote}
                            handleSubmit={handleSubmit}
                            isPreview={isPreview}
                            handleOptionsUpdate={handleOptionsUpdate}
                            isJustSubmitted={isJustSubmitted} />
                    }
                    {votingType === "MultiGroup" &&
                        <MultiGroupVote
                            ballot={ballot}
                            getGroup={getGroup}
                            currentGroup={currentGroup}
                            currentGroupVote={currentGroupVote}
                            groupIds={groupIds}
                            handleSubmit={handleSubmit}
                            handleSubmitVotes={handleSubmitVotes}
                            isPreview={isPreview}
                            previewId={previewId}
                            handleOptionsUpdate={handleOptionsUpdate}
                            handleVoteError={handleVoteError}
                            voter={voter}
                            isJustSubmitted={isJustSubmitted} />
                    }
                </div>}
        </div>
    );
}